/**
 * Overlap Policy Hook
 * Feature: 005-clinic-scheduling
 *
 * Read and update the organization's scheduling overlap policy.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth'
import type {
  SchedulingOverlapPolicy,
  UpdateOverlapPolicyInput,
  ScheduleOperationResult,
} from '../types/scheduling.types'

// ============================================================
// Fetch Function
// ============================================================

async function fetchOverlapPolicy(
  organizationId: string
): Promise<SchedulingOverlapPolicy> {
  const { data, error } = await supabase
    .from('organizations')
    .select('scheduling_overlap_policy')
    .eq('id', organizationId)
    .single()

  if (error) throw error
  return (data.scheduling_overlap_policy ?? 'allowed_with_warning') as SchedulingOverlapPolicy
}

// ============================================================
// Hooks
// ============================================================

export function useOverlapPolicy() {
  const { currentOrganization } = useAuth()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: ['overlap-policy', organizationId],
    queryFn: () => fetchOverlapPolicy(organizationId!),
    enabled: !!organizationId,
  })
}

export function useUpdateOverlapPolicy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      input: UpdateOverlapPolicyInput
    ): Promise<ScheduleOperationResult> => {
      const { error } = await supabase
        .from('organizations')
        .update({ scheduling_overlap_policy: input.policy })
        .eq('id', input.organization_id)

      if (error) return { success: false, error: error.message }
      return { success: true }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['overlap-policy', variables.organization_id],
      })
    },
  })
}
